import { supabase } from '@/lib/supabase';
import type { AuthError } from '@supabase/supabase-js';

type OAuthProvider = 'google' | 'github';

interface AuthResult {
  error: AuthError | null;
}

function hashEmail(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    const char = email.charCodeAt(i);
    hash = ((hash << 5) - hash + char) | 0;
  }
  return Math.abs(hash).toString(16).slice(0, 8);
}

function logAuthEvent(
  event: string,
  metadata?: { email?: string; provider?: string; error?: string },
) {
  const entry = {
    event,
    emailHash: metadata?.email ? hashEmail(metadata.email) : undefined,
    provider: metadata?.provider,
    timestamp: new Date().toISOString(),
    error: metadata?.error,
  };

  if (metadata?.error) {
    console.error('[auth]', entry);
  } else {
    console.info('[auth]', entry);
  }
}

export async function signIn(
  email: string,
  password: string,
): Promise<AuthResult> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  logAuthEvent(error ? 'sign_in_error' : 'sign_in', {
    email,
    error: error?.message,
  });
  return { error };
}

export async function signUp(
  email: string,
  password: string,
  fullName?: string,
): Promise<AuthResult> {
  const { error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: fullName ? { full_name: fullName } : undefined,
    },
  });
  logAuthEvent(error ? 'sign_up_error' : 'sign_up', {
    email,
    error: error?.message,
  });
  return { error };
}

export async function signOut(): Promise<AuthResult> {
  const { error } = await supabase.auth.signOut();
  logAuthEvent(error ? 'sign_out_error' : 'sign_out', {
    error: error?.message,
  });
  return { error };
}

export async function signInWithOAuth(
  provider: OAuthProvider,
): Promise<AuthResult> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  });
  logAuthEvent(error ? 'oauth_error' : 'oauth_redirect', {
    provider,
    error: error?.message,
  });
  return { error };
}

export async function signInWithOtp(email: string): Promise<AuthResult> {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: false,
    },
  });
  logAuthEvent(error ? 'magic_link_error' : 'magic_link_sent', {
    email,
    error: error?.message,
  });
  return { error };
}

export async function resetPassword(email: string): Promise<AuthResult> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/callback`,
  });
  logAuthEvent(error ? 'password_reset_error' : 'password_reset_sent', {
    email,
    error: error?.message,
  });
  return { error };
}
